import React from 'react';
import { Home, DollarSign, Users, House, BookOpen, Activity, Heart, Lock, Compass, Briefcase, TrendingUp, Sparkles } from 'lucide-react';

const ArudhaPadasDisplay = ({ arudhasData }) => {
  if (!arudhasData) return null;

  // Icon mapping for each Arudha
  const arudhaIcons = {
    "A1": Home,
    "A2": DollarSign,
    "A3": Users,
    "A4": House,
    "A5": BookOpen,
    "A6": Activity,
    "A7": Heart,
    "A8": Lock,
    "A9": Compass,
    "A10": Briefcase,
    "A11": TrendingUp,
    "A12": Sparkles
  };

  // Color gradients for each Arudha
  const arudhaColors = {
    "A1": "from-yellow-500 to-orange-500",
    "A2": "from-green-500 to-emerald-500",
    "A3": "from-blue-500 to-cyan-500",
    "A4": "from-purple-500 to-pink-500",
    "A5": "from-indigo-500 to-blue-500",
    "A6": "from-red-500 to-orange-500",
    "A7": "from-pink-500 to-rose-500",
    "A8": "from-gray-600 to-slate-700",
    "A9": "from-amber-500 to-yellow-500",
    "A10": "from-teal-500 to-cyan-600",
    "A11": "from-lime-500 to-green-500",
    "A12": "from-violet-500 to-purple-600"
  };

  const arudhaOrder = ["A1", "A2", "A3", "A4", "A5", "A6", "A7", "A8", "A9", "A10", "A11", "A12"];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center">
        <h2 className="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-pink-500 mb-2">
          Ārūḍha Padas (A1-A12)
        </h2>
        <p className="text-white/70 text-sm">
          Jaimini Predictive Points - Image vs Reality in 12 Life Areas
        </p>
      </div>

      {/* Grid of Arudha Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {arudhaOrder.map((arudhaKey) => {
          const arudha = arudhasData[arudhaKey];
          if (!arudha) return null;

          const Icon = arudhaIcons[arudhaKey];
          const colorGradient = arudhaColors[arudhaKey];
          const isSpecial = ["A1", "A7", "A10", "A12"].includes(arudhaKey); // AL, A7, A10, UL

          return (
            <div
              key={arudhaKey}
              className={`bg-gradient-to-br from-slate-900/80 to-slate-800/50 backdrop-blur-xl rounded-xl p-4 border-2 ${
                isSpecial ? 'border-yellow-500/50 shadow-lg shadow-yellow-500/20' : 'border-purple-500/30'
              } hover:scale-105 transition-transform duration-300`}
            >
              {/* Header with icon and badge */}
              <div className="flex items-center justify-between mb-3">
                <div className={`bg-gradient-to-r ${colorGradient} text-white px-3 py-1 rounded-full text-sm font-bold flex items-center gap-2`}>
                  {Icon && <Icon className="w-4 h-4" />}
                  {arudhaKey}
                  {isSpecial && <span className="text-xs">⭐</span>}
                </div>
              </div>

              {/* Sign Info */}
              <div className="mb-3">
                <div className="text-2xl font-bold text-cyan-400 mb-1">
                  {arudha.sign}
                </div>
                <div className="text-white/60 text-xs">
                  Sign Index: {arudha.sign_index}
                </div>
              </div>

              {/* Meaning */}
              <div className="bg-white/5 rounded-lg p-3 border border-white/10">
                <p className="text-white/80 text-sm leading-relaxed">
                  {arudha.meaning}
                </p>
              </div>

              {/* Special marker */}
              {isSpecial && (
                <div className="mt-2 text-yellow-400 text-xs font-medium text-center">
                  🌟 Key Arudha
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Key Interpretations */}
      <div className="bg-gradient-to-br from-indigo-900/30 to-purple-900/30 rounded-xl p-6 border border-indigo-500/30">
        <h3 className="text-indigo-300 text-xl font-bold mb-4">Key Arudha Interpretations</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-white/5 rounded-lg p-4">
            <h4 className="text-yellow-400 font-bold mb-2 flex items-center gap-2">
              <Home className="w-4 h-4" /> A1 (Arudha Lagna - AL)
            </h4>
            <p className="text-white/70 text-sm">
              Public image and how the world perceives you. Compare with actual Lagna to see image vs reality gap.
            </p>
          </div>
          
          <div className="bg-white/5 rounded-lg p-4">
            <h4 className="text-pink-400 font-bold mb-2 flex items-center gap-2">
              <Heart className="w-4 h-4" /> A7 (Marriage Arudha)
            </h4>
            <p className="text-white/70 text-sm">
              Public perception of marriage and partnerships. Shows how relationships appear to others.
            </p>
          </div>
          
          <div className="bg-white/5 rounded-lg p-4">
            <h4 className="text-teal-400 font-bold mb-2 flex items-center gap-2">
              <Briefcase className="w-4 h-4" /> A10 (Career Arudha)
            </h4>
            <p className="text-white/70 text-sm">
              Professional reputation and status. Indicates how career and achievements are perceived publicly.
            </p>
          </div>
          
          <div className="bg-white/5 rounded-lg p-4">
            <h4 className="text-violet-400 font-bold mb-2 flex items-center gap-2">
              <Sparkles className="w-4 h-4" /> A12 (Upapada - UL)
            </h4>
            <p className="text-white/70 text-sm">
              Marriage karma and spouse's nature. Critical for understanding relationship patterns and marital harmony.
            </p>
          </div>
        </div>
      </div>

      {/* Usage Guide */}
      <div className="bg-gradient-to-br from-slate-900/50 to-slate-800/30 rounded-xl p-6 border border-white/10">
        <h3 className="text-white text-lg font-bold mb-4">📚 How to Use Ārūḍha Padas</h3>
        <div className="space-y-3 text-sm text-white/70">
          <div className="flex items-start gap-2">
            <span className="text-yellow-400">•</span>
            <p><span className="text-cyan-400 font-medium">Compare with actual houses:</span> Gap between Arudha and actual house shows perception vs reality</p>
          </div>
          <div className="flex items-start gap-2">
            <span className="text-yellow-400">•</span>
            <p><span className="text-cyan-400 font-medium">Transits to Arudhas:</span> Events trigger when planets transit Arudha signs</p>
          </div>
          <div className="flex items-start gap-2">
            <span className="text-yellow-400">•</span>
            <p><span className="text-cyan-400 font-medium">Dasha connections:</span> Arudha lords' dashas bring manifestation of that life area</p>
          </div>
          <div className="flex items-start gap-2">
            <span className="text-yellow-400">•</span>
            <p><span className="text-cyan-400 font-medium">Key Arudhas (AL, A7, A10, UL):</span> Most important for life predictions and timing</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ArudhaPadasDisplay;
